import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { getChannelFacets, getGlobalFacets } from '../../api/search';

import { normalizeSidebarFacets } from '../../components/Sidebar/Facet/utils/normalizeSidebarFacets';

import { REQUEST } from '../../constants/request';

export const loadGlobalFacets = createAsyncThunk(
  'sidebarFacets/loadGlobalFacets',
  async (arg, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });

    try {
      const response = await getGlobalFacets({ cancelToken: source.token });
      const facets = response.data.result.root.find(
        i => i.id === 'group:root:0',
      );
      return facets.children[0].children;
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

export const loadChannelFacets = createAsyncThunk(
  'sidebarFacets/loadChannelFacets',
  async (args, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });

    try {
      const response = await getChannelFacets(args, {
        cancelToken: source.token,
      });

      return response.data.result.root.children[0]?.children;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const INITIAL_STATE = {
  sidebarFacets: {
    byId: {},
    allIds: [],
  },
  loading: REQUEST.idle,
  currentRequestId: undefined,
  isOpenLeftPanel: false,
};

export const SidebarFacetsSlice = createSlice({
  name: 'sidebarFacets',
  initialState: INITIAL_STATE,
  reducers: {
    setSidebarFacets: (state, { payload }) => {
      const { byId, allIds } = normalizeSidebarFacets(state, payload);

      state.sidebarFacets.byId = byId;
      state.sidebarFacets.allIds = allIds;
    },
    setIsOpenLeftPanel: (state, { payload }) => {
      state.isOpenLeftPanel = payload;
    },
    resetSidebarFacets: state => {
      state.sidebarFacets = INITIAL_STATE.sidebarFacets;
    },
  },
  extraReducers: builder => {
    // builder.addCase(loadGlobalFacets.fulfilled, (state, { payload, meta }) => {
    // payload == children of "group:root:0"
    // },
    builder.addCase(loadChannelFacets.pending, (state, { meta }) => {
      if (state.loading === REQUEST.idle) {
        state.loading = REQUEST.pending;
        state.currentRequestId = meta.requestId;
      }
    });
    builder.addCase(loadChannelFacets.fulfilled, (state, { payload, meta }) => {
      if (
        state.loading === REQUEST.pending &&
        state.currentRequestId === meta.requestId
      ) {
        const { byId, allIds } = normalizeSidebarFacets(state, payload);
        // TODO: remove when backend is ready
        byId.buzzes = {
          childrenAllIds: [
            'draft',
            'bookmark',
            'use case',
            'general',
            'thread',
          ],
          childrenById: {
            draft: {
              fields: { count: null },
              value: 'draft',
            },
            bookmark: {
              fields: { count: null },
              value: 'bookmark',
            },
            'use case': {
              fields: { count: null },
              value: 'use case',
            },
            general: {
              fields: { count: null },
              value: 'general',
            },
            thread: {
              fields: { count: null },
              value: 'thread',
            },
          },
          id: 'grouplist:buzzes',
          label: 'buzzes',
        };

        state.sidebarFacets.byId = byId;
        // TODO: fix when backend is ready
        state.sidebarFacets.allIds = [...allIds, 'buzzes'];
        state.loading = REQUEST.idle;
        state.currentRequestId = undefined;
      }
    });
    builder.addCase(
      loadChannelFacets.rejected,
      (state, { meta: { requestId }, error }) => {
        if (
          state.loading === REQUEST.pending &&
          state.currentRequestId === requestId
        ) {
          state.loading = REQUEST.idle;
          state.error = error.message;
          state.currentRequestId = undefined;
        }
      },
    );
  },
});

export const { setSidebarFacets, setIsOpenLeftPanel, resetSidebarFacets } =
  SidebarFacetsSlice.actions;

export const selectorIsOpenLeftPanel = state =>
  state.sidebarFacets.isOpenLeftPanel;
export const selectorSidebarFacets = state => state.sidebarFacets.sidebarFacets;
export const selectorGetFacet = (state, facetId) =>
  state.sidebarFacets.sidebarFacets.byId[facetId] || {
    label: '',
    childrenById: {},
    childrenAllIds: [],
  };
export default SidebarFacetsSlice.reducer;
